import { Icon } from '@glade-ai/ui';
import classNames from 'classnames';
import ProgressIndicator from 'components/ProgressIndicator';
import { m, useDragControls } from 'motion/react';
import {
  type CSSProperties,
  type PropsWithChildren,
  type ReactElement,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import DotsSixVertical from '@/components/Icons/DotsSixVertical';
import XCircle from '@/components/Icons/XCircle';
import DynamicDataPanel from '@/components/Questionnaire/form/ConnectedDataPanel';
import Spacer from '@/components/Spacer';
import isAndroid from '@/helpers/isAndroid';
import isInIframe from '@/helpers/isInIframe';
import isIOS from '@/helpers/isIOS';
import { useConfirm, useIsMobile } from '@/hooks';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';

import AiBackdrop from '../AiBackdrop';
import Portal from '../Portal';

import s from './Modal.module.scss';

type Props = {
  align?: 'left' | 'center' | 'right';
  onClose?: () => void;
  onMaximize?: () => void;
  title?: string | ReactElement;
  isFetching?: boolean;
  isValidating?: boolean;
  isAi?: boolean;
  isRightDrawer?: boolean;
  isWide?: boolean;
  isNarrow?: boolean;
  isFullScreen?: boolean;
  isMaximized?: boolean;
  actions?: Array<ReactElement>;
  hasPadding?: boolean;
  hasUnsavedChanges?: boolean;
  closeIcon?: ReactElement;
  containerStyle?: CSSProperties;
  zIndex?: number;
  hideBottomInset?: boolean;
  forcePinnedToBottom?: boolean;
  shouldCloseOnOutsideClick?: boolean;
  fixedHeight?: string;
  modalWidth?: string;
  modalHeight?: string;
  referencedListData?: {
    title: string;
    values: { property: string; value: string }[] | null;
    onEditClick?: () => void;
    type: 'autofill' | 'ai-autofill' | 'questionnaire' | 'dataset' | null;
  } | null;
  referencedListDataKey?: string;
  drag?: boolean;
  isTutorialVideoEnabled?: boolean;
  allowBackgroundInteraction?: boolean;
  disableModalBodyScroll?: boolean;
  shouldTruncateTitle?: boolean;
  /** @deprecated Use shouldTruncateTitle for clarity. */
  disableTitleTruncation?: boolean;
};

const Modal = forwardRef<HTMLDivElement, PropsWithChildren<Props>>(
  (
    {
      align,
      onClose,
      onMaximize,
      closeIcon = <XCircle weight='fill' size={24} color='var(--color-gray-100)' />,
      title,
      children,
      isFetching = false,
      isAi,
      isWide,
      isNarrow,
      isRightDrawer,
      actions = [],
      hasUnsavedChanges = false,
      isFullScreen = false,
      isMaximized = false,
      hasPadding = false,
      containerStyle = {},
      zIndex,
      hideBottomInset,
      forcePinnedToBottom = false,
      referencedListData,
      referencedListDataKey,
      fixedHeight,
      modalWidth,
      modalHeight,
      drag,
      isTutorialVideoEnabled = false,
      allowBackgroundInteraction = false,
      disableModalBodyScroll = false,
      isValidating = false,
      shouldTruncateTitle = false,
      disableTitleTruncation,
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const isPinnedToBottom = forcePinnedToBottom || isMobile;
    const isDraggable =
      drag && !isAndroid() && !isIOS() && !isRightDrawer && !isPinnedToBottom && !isFullScreen && !isAi;
    const dragControls = useDragControls();
    const constraintsRef = useRef(null);
    const previousOverflowRef = useRef<string | null>(null);
    const [isIconVisible, setIsIconVisible] = useState(false);
    const [isDragged, setIsDragged] = useState(false);
    const { isInDashboard, setModalOpen } = useContext(DashboardContext);

    const confirm = useConfirm();
    const shouldTruncateTitleValue =
      disableTitleTruncation === undefined ? shouldTruncateTitle : !disableTitleTruncation;

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const modalActions = [...actions];

    const style: CSSProperties = {};
    let containerStyleOverrides: CSSProperties = {};

    if (isFullScreen) {
      style.maxWidth = '100vw';
      style.maxHeight = 'min(min(100vh, 100dvh), var(--host-height))';
      containerStyleOverrides = {
        ...style,
        ...containerStyle,
      };
    }

    if (modalWidth) {
      containerStyleOverrides.width = modalWidth;
      containerStyleOverrides.maxWidth = 'calc(100vw - 2rem)';
    }

    if (modalHeight) {
      containerStyleOverrides.height = modalHeight;
      containerStyleOverrides.maxHeight = 'calc(100dvh - 2rem)';
    }

    if (isPinnedToBottom && !isInIframe()) {
      containerStyleOverrides.position = 'fixed';
      containerStyleOverrides.bottom = '0';
      containerStyleOverrides.width = '100vw';
      containerStyleOverrides.maxWidth = '100vw';
      containerStyleOverrides.borderRadius = '0';
      containerStyleOverrides.maxHeight = '100dvh';
      containerStyleOverrides.height = '100dvh';
    }

    useEffect(() => {
      setIsIconVisible(Boolean(onClose));
    }, [onClose]);

    useEffect(() => {
      if (isInDashboard) {
        setModalOpen(true);
        return () => {
          setModalOpen(false);
        };
      }
      return undefined;
    }, [isInDashboard, setModalOpen]);

    useEffect(() => {
      // Disable document body scroll for standard modals; keep background scrollable for the chat drawer
      const shouldLockDocumentBodyScroll = !allowBackgroundInteraction && !isDragged;
      if (shouldLockDocumentBodyScroll) {
        previousOverflowRef.current = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }
      return () => {
        // Directly setting document.body.style.overflow = 'auto' on cleanup may not restore the original value, using ref instead
        document.body.style.overflow = previousOverflowRef.current ?? 'auto';
      };
    }, [allowBackgroundInteraction, constraintsRef, isDragged]);

    const ESC_CONFIRM_MESSAGE = 'Are you sure you want to close this modal? Changes you made may not be saved.';

    const handleClose = useCallback(async () => {
      if (hasUnsavedChanges) {
        if (await confirm({ message: ESC_CONFIRM_MESSAGE })) {
          if (isInDashboard) {
            setModalOpen(false);
          }
          if (onCloseRef.current) {
            onCloseRef.current();
          }
        }
      } else if (onCloseRef.current) {
        if (isInDashboard) {
          setModalOpen(false);
        }
        onCloseRef.current();
      }
    }, [confirm, hasUnsavedChanges, isInDashboard, setModalOpen]);

    const handleClickIcon: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      (event) => {
        event.stopPropagation();
        event.preventDefault();
        void handleClose();
      },
      [handleClose],
    );

    const handleMaximizeClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(() => {
      if (onMaximize) {
        onMaximize();
      }
    }, [onMaximize]);

    const modalRef = useRef<HTMLDivElement>(null);

    /**
     * Ensure Esc closes the modal even when the modal has lost focus (allowBackgroundInteraction = true).
     * We attach a global keydown handler in capture phase so it fires regardless of focus and before bubbling handlers.
     * The local onKeyDown still handles Esc when focus is inside the modal; this listener is the fallback for background focus cases.
     */
    useEffect(() => {
      if (!allowBackgroundInteraction) return undefined;

      const onGlobalKeyDown = (e: KeyboardEvent): void => {
        // Check both key (logical) and code (physical) for external keyboard compatibility
        if (e.key === 'Escape' || e.code === 'Escape') {
          e.stopPropagation();
          void handleClose(); // No await needed for customer support chat as hasUnsavedChanges is false
        }
      };

      window.addEventListener('keydown', onGlobalKeyDown, true);
      return () => {
        window.removeEventListener('keydown', onGlobalKeyDown, true);
      };
    }, [allowBackgroundInteraction, handleClose]);

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        // Check both key (logical) and code (physical) for external keyboard compatibility
        if (event.key === 'Escape' || event.code === 'Escape') {
          event.stopPropagation();
          void handleClose();
          return;
        }
        if (event.key === 'Tab') {
          const modal = event.currentTarget;
          const elementsToSelect =
            'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])';
          const focusableElements = modal.querySelectorAll<HTMLElement>(elementsToSelect);
          if (focusableElements.length === 0) {
            event.preventDefault();
            return;
          }

          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement = focusableElements[focusableElements.length - 1];

          if (event.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              event.preventDefault();
              lastFocusableElement.focus();
            }
          } else if (document.activeElement === lastFocusableElement) {
            event.preventDefault();
            firstFocusableElement.focus();
          }
        }
      },
      [handleClose],
    );

    useEffect(() => {
      if (modalRef.current) {
        const elementsToSelect =
          'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), label';

        const focusableElements = Array.from(modalRef.current.querySelectorAll<HTMLElement>(elementsToSelect));

        // Find first element that's visible in the modal's viewport
        const firstVisibleElement = focusableElements.find((el) => {
          const rect = el.getBoundingClientRect();
          const modalRect = modalRef.current!.getBoundingClientRect();

          return (
            rect.top >= modalRect.top &&
            rect.bottom <= modalRect.bottom &&
            rect.left >= modalRect.left &&
            rect.right <= modalRect.right
          );
        });

        if (firstVisibleElement) {
          firstVisibleElement.focus();
        } else {
          modalRef.current.focus();
        }
      }
    }, []);

    return (
      <Portal>
        <m.div
          ref={constraintsRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={classNames(s.modal__backdrop, {
            [s['modal__backdrop--align-left']]: align === 'left',
            [s['modal__backdrop--align-right']]: align === 'right',
          })}
          style={{
            ...(isInIframe() && { alignItems: 'flex-start', paddingTop: 24 }),
            // For customer support chat, allow interacting with the app behind the drawer
            ...((allowBackgroundInteraction || isDragged) && { backgroundColor: 'transparent', pointerEvents: 'none' }),
            zIndex,
          }}
        >
          {isAi && <AiBackdrop />}
          <m.div
            ref={modalRef}
            initial={isAi || isRightDrawer ? { x: 100 } : undefined}
            animate={isAi || isRightDrawer ? { x: 0 } : false}
            transition={{
              ease: [0.25, 1, 0.5, 1],
            }}
            className={classNames(s.modal__container, {
              [s['modal__container--has-padding']]: hasPadding,
              [s['modal__container--ai']]: isAi,
              [s['modal__container--right']]: isRightDrawer,
              [s['modal__container--wide']]: isWide,
              [s['modal__container--narrow']]: isNarrow,
              [s['modal__container--resizable']]: isDraggable,
            })}
            style={{
              ...((allowBackgroundInteraction || isDragged) && {
                border: '1px solid var(--color-gray-25)',
                boxShadow: 'var(--shadow-lg)',
                pointerEvents: 'auto',
              }),
              ...containerStyleOverrides,
            }}
            drag={isDraggable}
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={constraintsRef}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            {isFetching && <ProgressIndicator isAbsolute />}
            {title !== null && title !== undefined && (
              <div
                className={s.header}
                style={{
                  ...(isFullScreen ? { borderRadius: 0 } : {}),
                  ...(onMaximize ? { gap: 25, justifyContent: 'space-between' } : {}),
                }}
              >
                <div style={isTutorialVideoEnabled ? { flexShrink: 0 } : undefined}>
                  {isIconVisible && (
                    <button
                      type='button'
                      className={s.closeButton}
                      onClick={handleClickIcon}
                      data-testid='dialog.closeButton'
                    >
                      {closeIcon}
                    </button>
                  )}
                </div>
                <div
                  style={{
                    ...(isTutorialVideoEnabled || isValidating
                      ? {
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textAlign: 'center',
                        }
                      : {}),
                  }}
                >
                  <div
                    className={classNames('body-sm', {
                      [s['title--ai']]: isAi,
                    })}
                    style={{
                      ...(isTutorialVideoEnabled ? { fontSize: '12px' } : {}),
                      ...(shouldTruncateTitleValue
                        ? {
                            maxWidth: 'calc(100% - 88px)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }
                        : {
                            maxWidth: 'none',
                            overflow: 'visible',
                            textOverflow: 'unset',
                            whiteSpace: 'normal',
                          }),
                    }}
                  >
                    {title}
                  </div>
                </div>
                {isValidating && (
                  <div
                    style={{ flexShrink: 0, marginInlineStart: 'auto' }}
                    role='status'
                    aria-label='Refreshing status'
                    aria-live='polite'
                  >
                    <Icon icon='ph--circle-notch--regular' className='animate-spin text-muted-foreground' size={4} />
                  </div>
                )}
                {isDraggable && (
                  <div
                    style={{
                      ...(isDraggable ? { cursor: 'grab' } : {}),
                      ...(isTutorialVideoEnabled ? { flexShrink: 0 } : {}),
                    }}
                    className={s.drag}
                    onPointerDown={(e) => {
                      if (isDraggable) {
                        dragControls.start(e);
                        setIsDragged(true);
                      }
                    }}
                  >
                    <DotsSixVertical />
                    Drag to move
                  </div>
                )}
                {onMaximize && !isPinnedToBottom && (
                  <button
                    type='button'
                    className={s.arrowsOutSimpleButton}
                    onClick={handleMaximizeClick}
                    aria-label={isMaximized ? 'Maximize video modal' : 'Minimize video modal'}
                    style={isTutorialVideoEnabled ? { flexShrink: 0 } : undefined}
                  >
                    {isMaximized ? (
                      <Icon icon='ph--arrows-in-simple--regular' size={5} />
                    ) : (
                      <Icon icon='ph--arrows-out-simple--regular' size={5} />
                    )}
                  </button>
                )}
              </div>
            )}
            <m.div
              id='modal-body'
              layoutScroll
              className={classNames(
                s.modal__body,
                disableModalBodyScroll && s['modal__body-no-scroll'],
                (isAi || isRightDrawer) && s['modal__body-right'],
              )}
              style={{
                ...style,
                ...(fixedHeight && { height: fixedHeight }),
              }}
              ref={ref}
            >
              {title && <Spacer size='24px' />}
              <div
                style={{
                  ...(referencedListData && { alignItems: 'flex-start', display: 'flex' }),
                  ...(fixedHeight && { minHeight: `calc(${fixedHeight} - 24px)` }),
                  ...(disableModalBodyScroll && {
                    height: title ? 'calc(100% - 24px)' : '100%',
                    minHeight: 0,
                  }),
                }}
              >
                {children}
                {referencedListData && (
                  <DynamicDataPanel key={referencedListDataKey || referencedListData.title} data={referencedListData} />
                )}
              </div>
              {((isPinnedToBottom && !hideBottomInset && !isInIframe()) ||
                (isPinnedToBottom && isAndroid()) ||
                (isPinnedToBottom && isIOS())) && <Spacer size='60px' />}
            </m.div>
            {modalActions.length > 0 && <div className={s.modal__actions}>{modalActions}</div>}
          </m.div>
        </m.div>
      </Portal>
    );
  },
);

Modal.displayName = 'Modal';

export default Modal;
